import { useState, useMemo } from 'react';
import Modal from '../common/Modal';
import { MODULE_TOOLS, type ToolGroup } from './module-tools';
import type { CategoryMeta } from '../../api/portal';

export interface ModuleInfo {
  id: string;
  name: string;
  description: string;
  category: 'base' | 'integration' | 'feature' | 'support' | 'governance' | 'tier';
  monthlyPrice: number;
  yearlyPrice: number;
  monthlyPriceCents: number;
  yearlyPriceCents: number;
  isPerSeat: boolean;
  isBase: boolean;
  displayOrder: number;
  iconName: string;
  toolCount: number;
  highlights?: string[];
}

interface Props {
  modules: ModuleInfo[];
  /** Category labels + descriptions sourced from CATEGORY_METADATA. Falls back to defaults if absent. */
  categories?: CategoryMeta[];
  selectedModules: string[];
  onSelectionChange: (moduleIds: string[]) => void;
  billingInterval: 'monthly' | 'yearly';
  seats: number;
  stakeholderSeats?: number;
  disabled?: boolean;
}

const FALLBACK_CATEGORIES: CategoryMeta[] = [
  { id: 'integration', label: 'Integrations', description: 'Connect your existing tools to ServiceNow', order: 0 },
  { id: 'feature', label: 'Features', description: 'Extend your development workflow', order: 1 },
  { id: 'support', label: 'Support', description: 'Get the help you need', order: 2 },
  { id: 'governance', label: 'Governance', description: 'Governance control plane — policy, audit, and multi-instance orchestration', order: 3 },
];

export const MODULE_ICONS: Record<string, JSX.Element> = {
  terminal: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  ticket: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
    </svg>
  ),
  azure: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
    </svg>
  ),
  book: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  ),
  github: (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
    </svg>
  ),
  gitlab: (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M22.65 14.39L12 22.13 1.35 14.39a.84.84 0 01-.3-.94l1.22-3.78 2.44-7.51A.42.42 0 014.82 2a.43.43 0 01.58 0 .42.42 0 01.11.18l2.44 7.49h8.1l2.44-7.51A.42.42 0 0118.6 2a.43.43 0 01.58 0 .42.42 0 01.11.18l2.44 7.51L23 13.45a.84.84 0 01-.35.94z" />
    </svg>
  ),
  chat: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  ),
  chart: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  headset: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
  'search-check': (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  ),
  shield: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3l8 4v5c0 5-3.5 8-8 9-4.5-1-8-4-8-9V7l8-4z" />
    </svg>
  ),
  network: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12a3 3 0 11-6 0 3 3 0 016 0zm12 0a3 3 0 11-6 0 3 3 0 016 0zm-9 9a3 3 0 11-6 0 3 3 0 016 0zm0-18a3 3 0 11-6 0 3 3 0 016 0zm3 9h0M9 6l3 3M9 18l3-3m3-3l3-3" />
    </svg>
  ),
  'file-check': (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5 6V7a2 2 0 00-2-2h-3.172a2 2 0 01-1.414-.586l-1.828-1.828A2 2 0 0010.172 2H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2z" />
    </svg>
  ),
  'git-branch': (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 3v12m0 0a3 3 0 103 3m-3-3a3 3 0 013 3m9-18a3 3 0 11-3 3m3-3a3 3 0 00-3 3m-3 0v6a3 3 0 01-3 3" />
    </svg>
  ),
  'brain-circuit': (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2a4 4 0 014 4v1a4 4 0 010 8v1a4 4 0 01-8 0v-1a4 4 0 010-8V6a4 4 0 014-4zm0 0v4m0 4v4m0 4v2M8 8h-2m14 0h-2M8 16h-2m14 0h-2" />
    </svg>
  ),
  store: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7h16l-1 4a3 3 0 01-6 0 3 3 0 01-6 0 3 3 0 01-6 0L4 7zm1 7v7h14v-7" />
    </svg>
  ),
};

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className || "w-3.5 h-3.5"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function ToolsModal({ moduleId, moduleName, toolGroups, isOpen, onClose }: {
  moduleId: string;
  moduleName: string;
  toolGroups: ToolGroup[];
  isOpen: boolean;
  onClose: () => void;
}) {
  const totalTools = toolGroups.reduce((sum, g) => sum + g.tools.length, 0);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`${moduleName} — ${totalTools} tools`} size="lg">
      <div className="space-y-6">
        {toolGroups.map((group, gi) => (
          <div key={gi}>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              {group.label}
              <span className="ml-1.5 text-gray-400 font-normal normal-case">({group.tools.length})</span>
            </h4>
            <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5">
              {group.tools.map((tool, ti) => (
                <div key={ti} className="flex items-start gap-2 py-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0 mt-1.5" />
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-gray-900">{tool.description}</span>
                    <span className="block text-xs text-gray-400 font-mono truncate">{tool.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}

export default function ModuleSelector({ modules, categories, selectedModules, onSelectionChange, billingInterval, seats, stakeholderSeats, disabled }: Props) {
  const [toolsModal, setToolsModal] = useState<{ moduleId: string; moduleName: string } | null>(null);
  const addOnModules = modules.filter(m => !m.isBase && (m.monthlyPrice > 0 || m.yearlyPrice > 0));
  const orderedCategories = (categories && categories.length > 0 ? categories : FALLBACK_CATEGORIES)
    .filter(c => c.id !== 'base' && c.id !== 'tier')
    .slice()
    .sort((a, b) => a.order - b.order);

  const grouped = useMemo(() => {
    const groups: Record<string, ModuleInfo[]> = {};
    for (const mod of addOnModules) {
      const cat = mod.category;
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(mod);
    }
    return groups;
  }, [addOnModules]);

  const toggleModule = (moduleId: string) => {
    if (disabled) return;
    if (selectedModules.includes(moduleId)) {
      onSelectionChange(selectedModules.filter(id => id !== moduleId));
    } else {
      onSelectionChange([...selectedModules, moduleId]);
    }
  };

  const extraStakeholders = Math.max(0, (stakeholderSeats || 0) - 1);
  const stakeholderPrice = billingInterval === 'monthly' ? 5 : 55;

  const { baseCost, modulesCost, stakeholderCost, totalCost } = useMemo(() => {
    const base = modules.find(m => m.isBase);
    const basePrice = base ? (billingInterval === 'monthly' ? base.monthlyPrice : base.yearlyPrice) : 0;
    const baseCost = basePrice * seats;

    let modCost = 0;
    for (const modId of selectedModules) {
      const mod = modules.find(m => m.id === modId && !m.isBase);
      if (mod) {
        const price = billingInterval === 'monthly' ? mod.monthlyPrice : mod.yearlyPrice;
        modCost += price * (mod.isPerSeat ? seats : 1);
      }
    }

    const shCost = extraStakeholders * stakeholderPrice;

    return { baseCost, modulesCost: modCost, stakeholderCost: shCost, totalCost: baseCost + modCost + shCost };
  }, [modules, selectedModules, billingInterval, seats, stakeholderSeats]);

  const interval = billingInterval === 'monthly' ? 'mo' : 'yr';
  const selectedAddOnCount = selectedModules.filter(id => {
    const m = modules.find(mod => mod.id === id);
    return m && !m.isBase;
  }).length;

  const openToolsModal = (e: React.MouseEvent, moduleId: string, moduleName: string) => {
    e.stopPropagation();
    setToolsModal({ moduleId, moduleName });
  };

  return (
    <div className="space-y-8">
      {/* Tools modal */}
      {toolsModal && MODULE_TOOLS[toolsModal.moduleId] && (
        <ToolsModal
          moduleId={toolsModal.moduleId}
          moduleName={toolsModal.moduleName}
          toolGroups={MODULE_TOOLS[toolsModal.moduleId]}
          isOpen={true}
          onClose={() => setToolsModal(null)}
        />
      )}

      {/* Base plan (always selected) */}
      {modules.filter(m => m.isBase).map(mod => (
        <div key={mod.id} className="p-5 bg-blue-50 border border-blue-200 rounded-xl">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 flex-shrink-0">
                {MODULE_ICONS[mod.iconName] || MODULE_ICONS.terminal}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-900">{mod.name}</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                    Included
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-1">{mod.description}</p>
                {mod.highlights && mod.highlights.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {mod.highlights.map((h, i) => (
                      <li key={i} className="flex items-center gap-1.5 text-xs text-blue-700">
                        <CheckIcon className="w-3 h-3 text-blue-500" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="font-semibold text-gray-900">
                €{billingInterval === 'monthly' ? mod.monthlyPrice : mod.yearlyPrice}
              </div>
              <div className="text-xs text-gray-500">/seat/{interval}</div>
            </div>
          </div>
        </div>
      ))}

      {/* Add-on modules by category */}
      {orderedCategories.map(cat => {
        const mods = grouped[cat.id];
        if (!mods || mods.length === 0) return null;

        return (
          <div key={cat.id}>
            <div className="mb-3">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                {cat.label}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {cat.description}
              </p>
            </div>
            <div className={`grid gap-3 ${mods.length === 1 ? 'grid-cols-1' : 'sm:grid-cols-2'}`}>
              {mods.map(mod => {
                const isSelected = selectedModules.includes(mod.id);
                const price = billingInterval === 'monthly' ? mod.monthlyPrice : mod.yearlyPrice;
                const highlights = mod.highlights || [];
                const isFeatured = mods.length === 1;
                const hasTools = !!MODULE_TOOLS[mod.id];

                return (
                  <button
                    key={mod.id}
                    onClick={() => toggleModule(mod.id)}
                    disabled={disabled}
                    className={`${isFeatured ? 'p-5' : 'p-4'} rounded-xl border-2 text-left transition-all ${
                      isSelected
                        ? isFeatured
                          ? 'border-purple-500 bg-purple-50 shadow-sm'
                          : 'border-blue-500 bg-blue-50/50 shadow-sm'
                        : isFeatured
                          ? 'border-purple-200 bg-purple-50/30 hover:border-purple-400'
                          : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                    } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    {/* Header row: checkbox + icon + name + price */}
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 border-2 transition-colors ${
                        isSelected
                          ? isFeatured
                            ? 'bg-purple-500 border-purple-500 text-white'
                            : 'bg-blue-500 border-blue-500 text-white'
                          : isFeatured
                            ? 'border-purple-300 bg-white'
                            : 'border-gray-300 bg-white'
                      }`}>
                        {isSelected && <CheckIcon className="w-3 h-3" />}
                      </div>
                      <div className={`${isFeatured ? 'w-10 h-10' : 'w-8 h-8'} rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isSelected
                          ? isFeatured ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'
                          : isFeatured ? 'bg-purple-100 text-purple-500' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {MODULE_ICONS[mod.iconName] || MODULE_ICONS.terminal}
                      </div>
                      <div className="flex-1">
                        <span className={`font-semibold text-gray-900 ${isFeatured ? 'text-base' : 'text-sm'}`}>{mod.name}</span>
                        {(isFeatured || price === 0) && <p className="text-sm text-gray-600 mt-0.5">{mod.description}</p>}
                      </div>
                      <div className="text-right flex-shrink-0">
                        {price === 0 ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Free</span>
                        ) : (
                          <>
                            <span className={`font-semibold text-gray-900 ${isFeatured ? 'text-base' : 'text-sm'}`}>+€{price}</span>
                            <span className={`text-gray-400 ${isFeatured ? 'text-sm' : 'text-xs'}`}>/{interval}</span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Benefit bullets */}
                    {highlights.length > 0 && (
                      <ul className={`mt-3 space-y-1.5 ${isFeatured ? 'ml-[3.25rem] grid sm:grid-cols-2 gap-x-6 gap-y-1.5 space-y-0' : 'ml-8'}`}>
                        {highlights.map((h, i) => (
                          <li key={i} className={`flex items-start gap-2 text-gray-600 ${isFeatured ? 'text-sm' : 'text-xs'}`}>
                            <CheckIcon className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${isSelected ? (isFeatured ? 'text-purple-500' : 'text-blue-500') : 'text-gray-400'}`} />
                            <span>{h}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Tool count badge + View tools link */}
                    {(mod.toolCount > 0 || hasTools) && (
                      <div className={`mt-2.5 flex items-center gap-3 ${isFeatured ? 'ml-[3.25rem]' : 'ml-8'}`}>
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
                          isSelected
                            ? isFeatured ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          {mod.toolCount} tools
                        </span>
                        {hasTools && (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => openToolsModal(e, mod.id, mod.name)}
                            onKeyDown={(e) => { if (e.key === 'Enter') openToolsModal(e as any, mod.id, mod.name); }}
                            className={`text-xs font-medium underline underline-offset-2 transition-colors ${
                              isSelected
                                ? isFeatured ? 'text-purple-600 hover:text-purple-700' : 'text-blue-600 hover:text-blue-700'
                                : 'text-gray-500 hover:text-gray-700'
                            }`}
                          >
                            View all tools
                          </span>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Total summary */}
      <div className="bg-gray-50 rounded-xl p-5 space-y-2">
        <div className="flex justify-between text-sm text-gray-600">
          <span>Base plan ({seats} {seats === 1 ? 'seat' : 'seats'})</span>
          <span>€{baseCost.toFixed(2)}</span>
        </div>
        {modulesCost > 0 && (
          <div className="flex justify-between text-sm text-gray-600">
            <span>Modules ({selectedAddOnCount} selected)</span>
            <span>€{modulesCost.toFixed(2)}</span>
          </div>
        )}
        {stakeholderCost > 0 && (
          <div className="flex justify-between text-sm text-gray-600">
            <span>Extra stakeholders ({extraStakeholders})</span>
            <span>€{stakeholderCost.toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between font-semibold text-gray-900 text-lg pt-3 border-t border-gray-200">
          <span>Total</span>
          <span>€{totalCost.toFixed(2)}<span className="text-sm font-normal text-gray-500">/{interval}</span></span>
        </div>
      </div>
    </div>
  );
}
